// Casio FX-991EX (ClassWiz) extensions — distributions, spreadsheet helpers, QR.
// Re-exports the FX-991ES engine and adds EX-only features.

export * from "./casio-fx991es";

// ---------------------------------------------------------------------------
// Statistical distributions
// ---------------------------------------------------------------------------

/** Standard normal PDF φ(z). */
export function normalPdf(z: number): number {
  return Math.exp(-(z * z) / 2) / Math.sqrt(2 * Math.PI);
}

/**
 * Standard normal CDF Φ(z) using the Abramowitz & Stegun 7.1.26 approximation
 * (max error ≈ 7.5e-8). Adequate for calculator-grade precision.
 */
export function normalCdf(z: number): number {
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  // erf approximation
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  const erf = sign * y;
  return 0.5 * (1 + erf);
}

/** Convert (x, μ, σ) → standard z-score and use normalCdf. */
export function normalCdfRange(a: number, b: number, mu = 0, sigma = 1): number {
  if (sigma <= 0) return NaN;
  const za = (a - mu) / sigma;
  const zb = (b - mu) / sigma;
  return normalCdf(zb) - normalCdf(za);
}

/** Binomial coefficient C(n, k) using multiplicative formula (no factorial overflow). */
export function binomCoef(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let c = 1;
  for (let i = 0; i < k; i++) c = (c * (n - i)) / (i + 1);
  return c;
}

/** Binomial PMF: P(X = k) for X ~ B(n, p). */
export function binomialPmf(n: number, p: number, k: number): number {
  if (n < 0 || k < 0 || k > n || p < 0 || p > 1) return NaN;
  return binomCoef(n, k) * Math.pow(p, k) * Math.pow(1 - p, n - k);
}

/** Binomial CDF: P(X ≤ k). */
export function binomialCdf(n: number, p: number, k: number): number {
  let s = 0;
  const top = Math.min(n, Math.floor(k));
  for (let i = 0; i <= top; i++) s += binomialPmf(n, p, i);
  return s;
}

/** Poisson PMF: P(X = k) for X ~ Poisson(λ). */
export function poissonPmf(lambda: number, k: number): number {
  if (lambda < 0 || k < 0 || !Number.isInteger(k)) return NaN;
  // Use log to avoid overflow for large k
  let logF = 0;
  for (let i = 2; i <= k; i++) logF += Math.log(i);
  const logP = -lambda + k * Math.log(lambda) - logF;
  return Math.exp(logP);
}

export function poissonCdf(lambda: number, k: number): number {
  let s = 0;
  for (let i = 0; i <= Math.floor(k); i++) s += poissonPmf(lambda, i);
  return s;
}

// ---------------------------------------------------------------------------
// Student t-distribution CDF — series approximation via incomplete beta.
// ---------------------------------------------------------------------------

/** Regularised incomplete beta I_x(a,b) via continued fraction (Lentz). */
function regIncompleteBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const lbeta = lgamma(a) + lgamma(b) - lgamma(a + b);
  const front = Math.exp(Math.log(x) * a + Math.log(1 - x) * b - lbeta) / a;
  // Lentz continued fraction
  let f = 1, c = 1, d = 0;
  for (let m = 0; m < 200; m++) {
    let num: number;
    if (m === 0) num = 1;
    else if (m % 2 === 0) {
      const k = m / 2;
      num = (k * (b - k) * x) / ((a + 2 * k - 1) * (a + 2 * k));
    } else {
      const k = (m - 1) / 2;
      num = -((a + k) * (a + b + k) * x) / ((a + 2 * k) * (a + 2 * k + 1));
    }
    d = 1 + num * d;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + num / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d;
    const delta = c * d;
    f *= delta;
    if (Math.abs(delta - 1) < 1e-12) break;
  }
  return front * (f - 1);
}

function lgamma(x: number): number {
  // Lanczos approximation
  const g = 7;
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028,
    771.32342877765313, -176.61502916214059, 12.507343278686905,
    -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
  ];
  if (x < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * x)) - lgamma(1 - x);
  }
  x -= 1;
  let a = c[0];
  const t = x + g + 0.5;
  for (let i = 1; i < g + 2; i++) a += c[i] / (x + i);
  return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t) - t + Math.log(a);
}

/** Student t CDF: P(T ≤ t) for df degrees of freedom. */
export function tCdf(t: number, df: number): number {
  if (df <= 0) return NaN;
  const x = df / (df + t * t);
  const ib = regIncompleteBeta(x, df / 2, 0.5);
  const cdf = 1 - 0.5 * ib;
  return t >= 0 ? cdf : 1 - cdf;
}
